/* =====================================================================
 * exportUsersXlsx — 앱 관리 · 사용자 목록 엑셀 내보내기
 *
 * AppManagePage 사용자 탭의 "내보내기" 액션에서 사용한다.
 * 한 사용자를 한 행(사용자 · 이메일 · 역할 · 플랜 · 가입일)으로 .xlsx 로 만든다.
 * 셀 스타일이 필요하므로 워크스페이스 공통 패턴인 xlsx-js-style 을 사용한다
 * (ExcelRenderer 와 동일한 import 스타일).
 * ===================================================================== */

import XLSX from 'xlsx-js-style';

/** 헤더 라벨(컬럼명). 호출부(i18n)에서 현지화한 문자열을 주입한다. */
export interface UserExportColumns {
  user: string;
  email: string;
  role: string;
  plan: string;
  joinDate: string;
}

/** 한 사용자 = 한 행. 표시용으로 이미 가공(현지화/포맷)된 문자열. */
export interface UserExportRow {
  user: string;
  email: string;
  role: string;
  plan: string;
  joinDate: string;
}

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** 헤더 행 스타일(굵게 + 연한 배경). xlsx-js-style cell.s 계약을 따른다. */
const HEADER_STYLE = {
  font: { bold: true },
  fill: { patternType: 'solid', fgColor: { rgb: 'F2F2F2' } },
};

/**
 * 헤더 + 사용자 행을 시트용 2차원 배열(array-of-arrays)로 만든다.
 * 첫 행은 헤더, 이후 한 사용자가 한 행(5개 컬럼 고정 순서)이다.
 * 순수 함수라 단위 테스트로 컬럼 계약을 고정한다.
 */
export function buildUsersAoa(
  headers: UserExportColumns,
  rows: UserExportRow[],
): string[][] {
  return [
    [headers.user, headers.email, headers.role, headers.plan, headers.joinDate],
    ...rows.map((r) => [r.user, r.email, r.role, r.plan, r.joinDate]),
  ];
}

/**
 * 사용자 목록을 .xlsx 파일로 만들어 브라우저 다운로드를 트리거한다.
 * @param headers  현지화된 컬럼 헤더 라벨
 * @param rows     표시용으로 가공된 사용자 행 배열
 * @param filename 다운로드 파일명(확장자 포함)
 */
export function exportUsersToXlsx(
  headers: UserExportColumns,
  rows: UserExportRow[],
  filename: string,
): void {
  const aoa = buildUsersAoa(headers, rows);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  /* 컬럼 폭(문자 단위) — 이메일/이름이 잘리지 않도록 */
  ws['!cols'] = [
    { wch: 22 }, // 사용자
    { wch: 30 }, // 이메일
    { wch: 12 }, // 역할
    { wch: 14 }, // 플랜
    { wch: 14 }, // 가입일
  ];

  /* 헤더 행(첫 행) 굵게 처리 */
  for (let c = 0; c < aoa[0].length; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    const cell = ws[addr];
    if (cell) cell.s = HEADER_STYLE;
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Users');

  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([out], { type: XLSX_MIME });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}
